import React, { useState } from 'react'
import { apiDelete, apiGet, apiPost, apiPut } from '../../generic/Api_Functions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import AlertUI from '../../generic/AlertUI'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import CreateUserPasswordDialog from './CreateUserPasswordDialog'
import PasswordIcon from '@mui/icons-material/Password'
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import { IconButton, Button } from '@mui/material'
import bcrypt from 'bcryptjs-react'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import UserInitialScreenTemplateDialog from '../../Mos/initialScreenTemplates/UserInitialScreenTemplateDialog'
import AlarmSubscriptionDialog from '../alarmsubscriptions/AlarmSubscriptionDialog'

const Users = () => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const [dialogStatus, setDialogStatus] = useState({ open: false, row: null })
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [selectedDialogTab, setSelectedDialogTab] = useState(0)
  const [templateUser, setTemplateUser] = useState(null)
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
  // S119 (D11/CA7): second of the two doors to the subscriptions of a subject.
  const [subscriptionsDialog, setSubscriptionsDialog] = useState({ open: false, userId: 0, userName: '' })
  const isUserAuthorized = useAuthorization()

  const columns = [{
    name: t_('Username'),
    field: 'username',
    charLimit: 70,
    mandatory: true,
    unique: true,
    edit: false
  },
  {
    name: t_('Name'),
    field: 'name',
    charLimit: 70,
    mandatory: true
  },
  {
    name: t_('Last name'),
    field: 'surname',
    charLimit: 70,
    mandatory: true
  },
  {
    name: t_('Phone'),
    field: 'phoneNumber',
    charLimit: 70
  },
  {
    name: t_('Email'),
    field: 'email',
    charLimit: 70
  },
  {
    name: t_('Client'),
    field: 'clients.name',
    type: 'smartMultiSelect',
    edit: {
      source: async () => {
        const clients = await apiGet('clientsBasicData')
        return clients
      },
      id: 'id',
      field: 'name'
    }
  },
  {
    name: t_('Profile'),
    field: 'profile.name',
    value: 'profile.id',
    type: 'select',
    edit: {
      source: async () => {
        const profileOptions = await apiGet('profilesNoPagination')
        return profileOptions
      },
      id: 'id',
      field: 'name'
    },
    mandatory: true
  },
  {
    name: t_('Allow change password'),
    field: 'allowChangePassword',
    defaultNewValue: true,
    type: 'boolean'
  }
  ]

  const actions = {
    edit: {
      fetchFunction: (row) => {
        return apiPut('user', row)
      }
    },
    create: {
      fetchFunction: (row) => {
        if (!row.clients || (typeof row.clients === 'object' && !Array.isArray(row.clients)) || row.clients.length === 0) {
          row.clients = []
        }
        return apiPost('user', row)
          .then(response => {
            setDialogStatus({ open: true, row: response })
          })
      }
    },
    delete: {
      fetchFunction: (rows) => {
        if (rows.length === 1) {
          return apiDelete('user/' + rows[0].id, rows)
        } else {
          return apiDelete('userMulti', rows)
        }
      }
    },
    custom: [
      {
        name: 'Password',
        render: (row) => {
          return (
            <IconButton>
              <PasswordIcon />
            </IconButton>
          )
        },
        onClick: (row) => {
          setDialogStatus({ open: true, row })
        }
      },
      {
        name: 'Initial screen templates',
        render: (row) => (
          <IconButton>
            <DashboardCustomizeIcon />
          </IconButton>
        ),
        onClick: (row) => {
          setTemplateUser(row)
          setTemplateDialogOpen(true)
        },
        disabled: !isUserAuthorized('MOS_EDIT')
      },
      {
        // S119: same row-action pattern as "Initial screen templates" above. No new permission
        // (DN-6): the guard is the one already protecting this screen, so there is no `disabled`.
        name: 'Alarm subscriptions',
        render: (row) => (
          <IconButton>
            <NotificationsActiveOutlinedIcon />
          </IconButton>
        ),
        onClick: (row) => {
          setSubscriptionsDialog({ open: true, userId: row.id, userName: row.username })
        }
      }
    ]
  }

  const handleSave = () => {
    if (selectedDialogTab === 0) {
      if (password.length < 5) {
        setPasswordError(t_('Password must have at least 5 characters'))
        return
      }

      if (password !== confirmPassword) {
        setPasswordError(t_("Passwords don't match"))
        return
      }
    }
    const salt = bcrypt.genSaltSync(10)
    const hashedPassword = bcrypt.hashSync(password, salt)

    apiPut('user/setPassword', { username: dialogStatus.row.username, hashedPassword, salt, challenge: null })
      .then(() => {
        setDialogStatus({ ...dialogStatus, open: false })
        showAlert({ severity: 'success', title: t_('Password setted correctly') })
      })
      .catch(() => {
        setDialogStatus({ ...dialogStatus, open: false })
        showAlert({ severity: 'error', title: t_('Error while setting password') })
      })

    resetPassword()
  }

  const resetPassword = () => {
    setPassword('')
    setConfirmPassword('')
    setPasswordError('')
  }

  const fetchFunction = async (pagination) => {
    const users = await apiGet('user', pagination)
    return users
  }
  return (
    <>

      <SmartDialog
        renderComponent={
          <CreateUserPasswordDialog
            password={password}
            setPassword={setPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
            passwordError={passwordError}
            setSelectedDialogTab={setSelectedDialogTab}
            resetPassword={resetPassword}
          />
}
        setOpen={dialogStatus.open}
        cancel={false}
        accept={false}
        close
        closeCallback={() => {
          resetPassword()
          showAlert({ severity: 'info', title: t_("Password hasn't been setted") })
          if (dialogStatus.open) {
            setDialogStatus({ ...dialogStatus, open: false })
          }
        }}
        renderCustomButtons={
          <Button
            variant='contained'
            color='primary'
            onClick={handleSave}
            style={{ marginRight: '10px' }}
          >
            {t_('Save')}
          </Button>
        }
      />
      {AlertElement}
      <SmartMateriaUITable
        columns={columns}
        title={t_('Users')}
        dataFetch={fetchFunction}
        actions={actions}
        multipleSelection
        multipleOptions
        autocompleteAddButtonDisabled
        pageable
      />
      <UserInitialScreenTemplateDialog
        open={templateDialogOpen}
        setOpen={setTemplateDialogOpen}
        user={templateUser}
        onApplied={() => showAlert({ title: t_('Template applied'), severity: 'success' })}
      />
      <SmartDialog
        setOpen={subscriptionsDialog.open}
        accept={false}
        close
        cancel={false}
        closeCallback={() => setSubscriptionsDialog({ ...subscriptionsDialog, open: false })}
        renderComponent={
          <AlarmSubscriptionDialog
            subjectType='USER'
            subjectId={subscriptionsDialog.userId}
            subjectName={subscriptionsDialog.userName}
          />
        }
      />
    </>
  )
}

export default Users
